import React, { useEffect } from "react";
import { TextStyle, View, ViewStyle } from "react-native";
import { useSelector } from "react-redux";
import updateIcon from "@/assets/update-all.png";
import RemoveItem from "./RemoveItem";
import { editAllStyles as styles } from "./styles";
import TposButton from "../../Buttons/TposButton";
import { TposIcon } from "../../Icons/TposIcon";
import ProductAmount from "./ProductAmount";
import { userLineItems } from "@/app/selectors/lineItem";
import AboutProduct from "./AboutProduct";
import { Product } from "@/app/types/product";
import ItemOptions from "../ItemOptions";
import { OptionsType } from "@/app/types/option";
import editIcon from "@/assets/customise-individually.png";
import { useTranslateText } from "@/app/utils/translations";

type EditAllProps = {
  lineItemId: string;
  variationItem: Product;
  currentLineOptions: OptionsType[];
  onDeletePress: () => void;
  onQuantityChange: () => void;
  onUpdateAllItemsPress: () => void;
  setShowCustomiseIndividual: () => void;
  quantity: number;
  setQuantity: (quantity: number) => void;
};

const EditAll = ({
  lineItemId,
  variationItem,
  currentLineOptions,
  onDeletePress,
  onQuantityChange,
  onUpdateAllItemsPress,
  setShowCustomiseIndividual,
  quantity,
  setQuantity,
}: EditAllProps): JSX.Element => {
  const { t } = useTranslateText();
  const minimumQuantity = 1;

  const lineItems = useSelector(userLineItems);
  const lineItem =
    lineItems && lineItems.find((v) => lineItemId && `${v.id}` === lineItemId);

  const customiseButtonDisabled = !!lineItem && lineItem.quantity <= 1;

  useEffect(() => {
    if (lineItem?.quantity !== quantity) {
      onQuantityChange();
    }
  }, [quantity, lineItem?.quantity, onQuantityChange]);

  return (
    <View
      style={styles.container}
      accessibilityRole="alert"
      accessibilityLabel="Edit basket items"
      testID="edit-all-modal"
      accessible={true}
    >
      <View style={styles.header}>
        <View style={styles.aboutContainer}>
          <AboutProduct item={variationItem} />
        </View>
        <View style={styles.removeButtonContainer}>
          <RemoveItem onDeletePress={onDeletePress} />
        </View>
      </View>
      <View style={styles.bottomContainer}>
        <ItemOptions
          lineItemId={Number(lineItemId)}
          defaultOptions={currentLineOptions}
        />
        <View style={styles.quantityLayoutContainer}>
          <View style={styles.quantityButtonContainer}>
            <ProductAmount
              minimumQuantity={minimumQuantity}
              quantity={quantity}
              onReduce={() =>
                setQuantity(
                  quantity > minimumQuantity ? quantity - 1 : minimumQuantity,
                )
              }
              onAdd={() => setQuantity(quantity + 1)}
            />
          </View>
          <View style={styles.buttonItemContainer}>
            <TposButton
              styles={{
                buttonContainer: styles.editButtonContainer,
                disabledContainer: styles.editButtonContainer,
              }}
              onPress={setShowCustomiseIndividual}
              disabled={customiseButtonDisabled}
              buttonText={t("tpos.productArea.customiseIndividual")}
            >
              <TposIcon icon={editIcon} />
            </TposButton>
          </View>
        </View>
        <TposButton
          styles={{
            buttonText: styles.updateButtonText as TextStyle,
            buttonContainer: styles.updateButtonContainer as ViewStyle,
          }}
          onPress={onUpdateAllItemsPress}
          buttonText="Update All"
          accessibilityLabel="Update all items"
          accessibilityHint="Updates quantity for all similar items in basket"
          testID="update-all-button"
        >
          <TposIcon icon={updateIcon} />
        </TposButton>
      </View>
    </View>
  );
};

export default EditAll;
